import React, { useState, useEffect, useContext } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
// import { useLanguage } from '../../contexts/LanguageContext';
import { CartContext } from '../../App';
import ProductCard from '../../components/ProductCard';
import PriceRangeSlider from '../../components/PriceRangeSlider';
import apiInstance from '../../utils/axios';
import Swal from 'sweetalert2';
import UserData from '../plugin/UserData';
import getCartID from '../plugin/cartID';

const Products = () => {
  const [cartCount, updateCartCountAndNotify] = useContext(CartContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  
  // Get user data and cart ID
  const userData = UserData();
  const cart_id = getCartID();
  const [enhancedProducts, setEnhancedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [priceRange, setPriceRange] = useState({ min: 0, max: 1000 });
  const [priceSlider, setPriceSlider] = useState([0, 1000]);
  const [showPromotionsOnly, setShowPromotionsOnly] = useState(false);
  const [inStockOnly, setInStockOnly] = useState(false);




  // Memoized unique colors and sizes for better performance, filtered by selected category
  const filteredCategoryProducts = React.useMemo(() => {
    if (!selectedCategory || selectedCategory === '') return products;
    return products.filter(product => {
      const productCategoryTitle = product.category?.title || '';
      const productCategoryTranslated = product.category?.title_translated || '';
      return productCategoryTitle === selectedCategory || productCategoryTranslated === selectedCategory;
    });
  }, [products, selectedCategory]);

  // Memoized min/max price for slider, filtered by selected category
  const minMaxPrice = React.useMemo(() => {
    if (!filteredCategoryProducts || !filteredCategoryProducts.length) return { min: 0, max: 1000 };
    const prices = filteredCategoryProducts.map(p => p.price);
    return { min: Math.min(...prices), max: Math.max(...prices) };
  }, [filteredCategoryProducts]);

  // Sync slider and priceRange state
  useEffect(() => {
    setPriceSlider([minMaxPrice.min, minMaxPrice.max]);
    setPriceRange({ min: minMaxPrice.min, max: minMaxPrice.max });
  }, [minMaxPrice.min, minMaxPrice.max]);

  useEffect(() => {
    setPriceRange({ min: priceSlider[0], max: priceSlider[1] });
  }, [priceSlider]);
  const [selectedColors, setSelectedColors] = useState([]);
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Update category filter without page refresh
  const handleCategoryChange = (categoryTitle) => {
    setSelectedCategory(categoryTitle);
    // No URL update needed - filtering happens client-side
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSelectedCategory('');
    setPriceRange({ min: 0, max: 1000 });
    setSelectedColors([]);
    setSelectedSizes([]);
    setSearchTerm('');
    setShowPromotionsOnly(false);
    setInStockOnly(false);
    // No URL update needed - filtering happens client-side
  };

  // Initialize promotion filter from URL parameter
  useEffect(() => {
    const promotionParam = searchParams.get('promotion');
    if (promotionParam === 'true') {
      setShowPromotionsOnly(true);
    }
  }, [searchParams]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch products once on component mount
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await apiInstance.get('products/');
        const data = response.data;
        setProducts(data.results || data);
        
        // Initialize enhanced products with quantity tracking
        const enhanced = (data.results || data).map(product => ({
          ...product,
          quantity: 1
        }));
        setEnhancedProducts(enhanced);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Initialize category from URL parameter (only on initial load)
  useEffect(() => {
    const categoryParam = searchParams.get('category');
    if (categoryParam && categories.length > 0) {
      // Convert category ID to category name
      const categoryId = parseInt(categoryParam);
      const category = categories.find(cat => cat.id === categoryId);
      if (category) {
        // Use the translated title if available, otherwise use the regular title
        setSelectedCategory(category.title_translated || category.title);
      }
    }
  }, [categories, searchParams]); // Keep searchParams to handle URL changes

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await apiInstance.get('category/');
        const data = response.data;
        setCategories(data.results || data);
      } catch (err) {
        console.error('Failed to fetch categories:', err);
      }
    };

    fetchCategories();
  }, []);

  // Memoized filtered and sorted products for better performance
  const filteredProducts = React.useMemo(() => {
    return products
      .filter(product => {
        // Promotion filter
        if (showPromotionsOnly) {
          const hasDiscount = product.old_price && parseFloat(product.old_price) > parseFloat(product.price);
          if (!hasDiscount) return false;
        }
        
        // Stock filter
        if (inStockOnly && product.stock_qty <= 0) {
          return false;
        }
        
        // Category filter - check both title and title_translated
        if (selectedCategory && selectedCategory !== '') {
          const productCategoryTitle = product.category?.title || '';
          const productCategoryTranslated = product.category?.title_translated || '';
          const matches = productCategoryTitle === selectedCategory || productCategoryTranslated === selectedCategory;
          if (!matches) {
            return false;
          }
        }
      
      // Price filter
      if (product.price < priceRange.min || product.price > priceRange.max) {
        return false;
      }
      
      // Color filter
      if (selectedColors.length > 0) {
        const productColors = product.colors?.map(c => c.name) || [];
        if (!selectedColors.some(color => productColors.includes(color))) {
          return false;
        }
      }
      
      // Size filter
      if (selectedSizes.length > 0) {
        const productSizes = product.sizes?.map(s => s.name) || [];
        if (!selectedSizes.some(size => productSizes.includes(size))) {
          return false;
        }
      }
      
      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        return (
          (product.title_translated || product.title).toLowerCase().includes(searchLower) ||
          (product.description_translated || product.description)?.toLowerCase().includes(searchLower)
        );
      }
      
      return true;
    })
    .sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];
      
      if (sortBy === 'price') {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  }, [products, selectedCategory, priceRange, selectedColors, selectedSizes, searchTerm, sortBy, sortOrder, showPromotionsOnly, inStockOnly]);



  const allColors = React.useMemo(() => {
    return [...new Set(filteredCategoryProducts.flatMap(p => p.colors?.map(c => c.name) || []))];
  }, [filteredCategoryProducts]);

  const allSizes = React.useMemo(() => {
    return [...new Set(filteredCategoryProducts.flatMap(p => p.sizes?.map(s => s.name) || []))];
  }, [filteredCategoryProducts]);

  // Check if selected category has price, color, or size options
  const showPriceFilter = React.useMemo(() => filteredCategoryProducts.length > 0, [filteredCategoryProducts]);
  const showColorFilter = React.useMemo(() => allColors.length > 0, [allColors]);
  const showSizeFilter = React.useMemo(() => allSizes.length > 0, [allSizes]);

  // Memoized quantity change handler for better performance
  const handleQuantityChange = React.useCallback((productId, change) => {
    setEnhancedProducts(prev => 
      prev.map(product => 
        product.id === productId 
          ? { ...product, quantity: Math.max(1, product.quantity + change) }
          : product
      )
    );
  }, []);

  // Memoized add to cart handler for better performance
  const handleAddToCartFromCard = React.useCallback(async (product) => {
    const enhancedProduct = enhancedProducts.find(ep => ep.id === product.id) || product;

    // Check if product is in stock
    if (enhancedProduct.stock_qty <= 0) {
      Swal.fire({
        icon: 'error',
        title: 'Producto Agotado',
      });
      return;
    }

    const formdata = new FormData();
    formdata.append('product_id', enhancedProduct.id);
    formdata.append('user_id', userData?.user_id || '');
    formdata.append('qty', enhancedProduct.quantity || 1);
    formdata.append('price', enhancedProduct.price);
    formdata.append('shipping_amount', enhancedProduct.shipping_amount || 0);
    formdata.append('cart_id', cart_id);

    try {
      const response = await apiInstance.post('cart-view/', formdata);
      Swal.fire({
        icon: 'success',
        title: 'Agregado al Carrito',
      });

      // Update cart count
      const url = userData 
        ? `cart-list/${cart_id}/${userData.user_id}/` 
        : `cart-list/${cart_id}/`;
      const cartResponse = await apiInstance.get(url);
      updateCartCountAndNotify(cartResponse.data.length);
    } catch (error) {
      console.error('Error adding to cart:', error);
      
      let errorMessage = 'Error';
      if (error.response && error.response.data && error.response.data.error) {
        errorMessage = error.response.data.error;
      }
      
      Swal.fire({
        icon: 'error',
        title: errorMessage,
      });
    }
  }, [enhancedProducts, userData, cart_id, updateCartCountAndNotify]);

  // Memoized utility functions for better performance
  const canAddToCart = React.useCallback((product) => {
    return product.stock_qty > 0;
  }, []);

  const getAddToCartButtonText = React.useCallback((product) => {
    if (product.stock_qty <= 0) {
      return 'Agotado';
    }
    return 'Agregar al Carrito';
  }, []);


  if (loading) {
    return (
      <div className="products-page">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="products-layout flex gap-8">
            {/* Skeleton Filters Sidebar */}
            <div className="filters-sidebar">
              <div className="skeleton-title" style={{width: '80px', height: '20px', marginBottom: '30px'}}></div>
              
              {/* Search skeleton */}
              <div className="skeleton-description" style={{width: '60px', height: '14px', marginBottom: '8px'}}></div>
              <div className="skeleton-description" style={{width: '100%', height: '40px', marginBottom: '24px'}}></div>
              
              {/* Category skeleton */}
              <div className="skeleton-description" style={{width: '70px', height: '14px', marginBottom: '8px'}}></div>
              <div className="skeleton-description" style={{width: '100%', height: '40px', marginBottom: '24px'}}></div>
              
              {/* Price range skeleton */}
              <div className="skeleton-description" style={{width: '80px', height: '14px', marginBottom: '8px'}}></div>
              <div style={{display: 'flex', gap: '8px', marginBottom: '24px'}}>
                <div className="skeleton-description" style={{width: '50%', height: '40px'}}></div>
                <div className="skeleton-description" style={{width: '50%', height: '40px'}}></div>
              </div>
              
              {/* Clear filters button skeleton */}
              <div className="skeleton-price" style={{width: '100%', height: '40px'}}></div>
            </div>

            {/* Main Content */}
            <main className="flex-1">
              {/* Header skeleton */}
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                <div>
                  <div className="skeleton-title" style={{width: '120px', height: '28px', marginBottom: '8px'}}></div>
                  <div className="skeleton-description" style={{width: '100px', height: '16px'}}></div>
                </div>
                
                <div className="flex flex-col md:flex-row gap-4">
                  {/* Mobile filter button skeleton */}
                  <div className="skeleton-description" style={{width: '80px', height: '36px', display: 'block'}}></div>
                  
                  {/* Sort options skeleton */}
                  <div className="flex gap-2">
                    <div className="skeleton-description" style={{width: '100px', height: '36px'}}></div>
                    <div className="skeleton-description" style={{width: '120px', height: '36px'}}></div>
                  </div>
                </div>
              </div>
              
              {/* Skeleton Products Grid */}
              <div className="products-grid">
                {[...Array(6)].map((_, index) => (
                  <div key={index} className="skeleton-card">
                    <div className="skeleton-image"></div>
                    <div className="skeleton-title"></div>
                    <div className="skeleton-description"></div>
                    <div className="skeleton-description" style={{width: '60%'}}></div>
                    <div className="skeleton-price"></div>
                  </div>
                ))}
              </div>
            </main>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-red-500 text-lg">{error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200">
      {/* Hero Banner */}
      <div style={{background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)'}} className="text-white py-8 px-4 mb-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold mb-2">
                🔧 Herramientas Tayrun
              </h1>
              <p style={{color: '#94a3b8'}} className="text-lg">
                {showPromotionsOnly ? '🔥 Ofertas especiales seleccionadas' : 'Encuentra las mejores herramientas profesionales'}
              </p>
            </div>
            {showPromotionsOnly && (
              <div className="bg-white/10 backdrop-blur-sm rounded-xl px-6 py-3 border border-white/20">
                <span className="text-2xl mr-2">🏷️</span>
                <span className="font-bold text-xl">OFERTAS ACTIVAS</span>
              </div>
            )}
          </div>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-1 md:px-4 pb-8">
        <div className="bg-white rounded-2xl shadow-lg border border-white/80 p-4 md:p-8">
          <div className="flex flex-col md:flex-row gap-6 md:gap-10">
            {/* Filters Sidebar */}
            <aside className={`transition-all duration-300 z-20 bg-gradient-to-br from-white to-slate-50 rounded-xl border border-slate-200 p-4 md:p-7 w-full md:w-80 h-fit md:sticky md:top-8 ${showFilters ? 'fixed left-0 top-0 w-11/12 h-full shadow-2xl' : 'hidden md:block'}`}> 
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-lg md:text-xl font-bold text-slate-800 flex items-center gap-2">
                  <span>⚙️</span> Filtros
                </h2>
                <button
                  onClick={() => setShowFilters(false)}
                  className="md:hidden text-gray-500 hover:text-gray-700 text-2xl font-bold"
                  aria-label="Close filters"
                >
                  ✕
                </button>
              </div>
            {/* Search */}
            <div className="mb-6">
              <label className="block text-sm font-medium mb-2 text-slate-700">Buscar</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar productos..."
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
              />
            </div>
            
            {/* Quick Filters - Promotions & Stock */}
            <div className="mb-6 p-4 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-xl border border-orange-100">
              <label className="block text-sm font-bold mb-3 text-slate-700">🔥 Filtros Rápidos</label>
              
              {/* Promotion Toggle */}
              <label className="flex items-center justify-between cursor-pointer p-3 bg-white rounded-lg shadow-sm mb-2 hover:shadow-md transition-all">
                <span className="flex items-center gap-2 text-sm font-medium text-slate-700">
                  <span className="text-lg">🏷️</span>
                  Solo ofertas
                </span>
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={showPromotionsOnly}
                    onChange={(e) => setShowPromotionsOnly(e.target.checked)}
                    className="sr-only"
                  />
                  <div className={`w-11 h-6 rounded-full transition-colors ${showPromotionsOnly ? 'bg-gradient-to-r from-orange-400 to-red-400' : 'bg-slate-300'}`}>
                    <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${showPromotionsOnly ? 'translate-x-5' : ''}`}></div>
                  </div>
                </div>
              </label>
              
              {/* Stock Toggle */}
              <label className="flex items-center justify-between cursor-pointer p-3 bg-white rounded-lg shadow-sm hover:shadow-md transition-all">
                <span className="flex items-center gap-2 text-sm font-medium text-slate-700">
                  <span className="text-lg">📦</span>
                  En stock
                </span>
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={inStockOnly}
                    onChange={(e) => setInStockOnly(e.target.checked)}
                    className="sr-only"
                  />
                  <div className={`w-11 h-6 rounded-full transition-colors ${inStockOnly ? 'bg-gradient-to-r from-green-400 to-emerald-400' : 'bg-slate-300'}`}>
                    <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${inStockOnly ? 'translate-x-5' : ''}`}></div>
                  </div>
                </div>
              </label>
            </div>
            
            {/* Category Filter */}
            <div className="mb-6">
              <label className="block text-sm font-medium mb-2 text-slate-700">Categoría</label>
              <div className="space-y-2">
                <label className="flex items-center cursor-pointer hover:bg-slate-50 p-2 rounded-md transition-colors">
                  <input
                    type="checkbox"
                    checked={selectedCategory === ''}
                    onChange={() => handleCategoryChange('')}
                    className="mr-3 h-4 w-4 text-blue-500 focus:ring-blue-400 border-slate-300 rounded"
                  />
                  <span className="text-sm font-medium text-slate-700">Todas las categorías</span>
                </label>
                {categories.map(category => (
                  <label key={category.id} className="flex items-center cursor-pointer hover:bg-slate-50 p-2 rounded-md transition-colors">
                    <input
                      type="checkbox"
                      checked={selectedCategory === (category.title_translated || category.title)}
                      onChange={() => handleCategoryChange(category.title_translated || category.title)}
                      className="mr-3 h-4 w-4 text-blue-500 focus:ring-blue-400 border-slate-300 rounded"
                    />
                    <span className="text-sm font-medium text-slate-700">{category.title_translated || category.title}</span>
                  </label>
                ))}
              </div>
            </div>
            {/* Price Range Slider */}
            {showPriceFilter && (
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2 text-slate-700">Rango de precios</label>
                <PriceRangeSlider
                  min={minMaxPrice.min}
                  max={minMaxPrice.max}
                  value={priceSlider}
                  onChange={setPriceSlider}
                />
                <div className="flex justify-between text-xs mt-2 text-slate-600">
                  <span>Mín: {priceSlider[0]}</span>
                  <span>Máx: {priceSlider[1]}</span>
                </div>
              </div>
            )}
            {/* Colors */}
            {showColorFilter && (
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2 text-slate-700">Colores</label>
                <div className="space-y-2">
                  {allColors.map(color => (
                    <label key={color} className="flex items-center cursor-pointer hover:bg-slate-50 p-2 rounded-md transition-colors">
                      <input
                        type="checkbox"
                        checked={selectedColors.includes(color)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedColors(prev => [...prev, color]);
                          } else {
                            setSelectedColors(prev => prev.filter(c => c !== color));
                          }
                        }}
                        className="mr-3 h-4 w-4 text-blue-500 focus:ring-blue-400 border-slate-300 rounded"
                      />
                      <span className="text-sm font-medium text-slate-700">{color}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            {/* Sizes */}
            {showSizeFilter && (
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2 text-slate-700">Tamaños</label>
                <div className="space-y-2">
                  {allSizes.map(size => (
                    <label key={size} className="flex items-center cursor-pointer hover:bg-slate-50 p-2 rounded-md transition-colors">
                      <input
                        type="checkbox"
                        checked={selectedSizes.includes(size)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedSizes(prev => [...prev, size]);
                          } else {
                            setSelectedSizes(prev => prev.filter(s => s !== size));
                          }
                        }}
                        className="mr-3 h-4 w-4 text-blue-500 focus:ring-blue-400 border-slate-300 rounded"
                      />
                      <span className="text-sm font-medium text-slate-700">{size}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            {/* Clear Filters */}
            <button 
              onClick={clearAllFilters}
              className="w-full px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors font-semibold mt-2"
            >
              Limpiar filtros
            </button>
          </aside>
          {/* Main Content */}
          <main className="flex-1">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
              <div>
                <h1 className="text-2xl font-bold mb-2 text-slate-800">Productos</h1>
                <p className="text-slate-500 text-sm">{filteredProducts.length} productos encontrados</p>
              </div>
              <div className="flex flex-col md:flex-row gap-4">
                {/* Mobile Filter Toggle */}
                <button
                  onClick={() => setShowFilters(true)}
                  className="md:hidden px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-semibold"
                >
                  Filtros
                </button>
                {/* Sort Options */}
                <div className="flex gap-2">
                  <select 
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
                  >
                    <option value="name">Nombre</option>
                    <option value="price">Precio</option>
                    <option value="created_at">Más nuevo</option>
                  </select>
                  <select
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value)}
                    className="px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
                  >
                    <option value="asc">Ascendente</option>
                    <option value="desc">Descendente</option>
                  </select>
                </div>
              </div>
            </div>
            {/* Products Grid */}
            {filteredProducts.length === 0 ? (
              <div className="text-center py-12 bg-white/80 rounded-xl">
                <div className="text-6xl mb-4">🔍</div>
                <p className="text-slate-400 text-lg font-semibold mb-2">No se encontraron productos</p>
                <p className="text-slate-400 text-sm mb-4">Intenta ajustar los filtros o buscar otro término</p>
                <button 
                  onClick={clearAllFilters}
                  className="mt-4 px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-lg hover:from-blue-600 hover:to-indigo-600 transition-all font-semibold shadow-lg hover:shadow-xl"
                >
                  🔄 Limpiar filtros
                </button>
              </div>
            ) : (
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 items-start">
                {filteredProducts.map((product) => {
                  const enhancedProduct = enhancedProducts.find(ep => ep.id === product.id) || product;
                  return (
                    <ProductCard
                      key={product.id}
                      product={enhancedProduct}
                      variant="default"
                      showDiscount={true}
                      showShipping={true}
                      className="min-h-[540px] w-full"
                      style={{}}
                    />
                  );
                })}
              </div>
            )}
          </main>
        </div>
        </div>
      </div>
    </div>
  );
};

export default Products;
